import { ReactNode } from "react";
import { usePermissions } from "../../hooks/usePermissions";

interface PermissionGateProps {
  /**
   * Single permission required to render children
   */
  permission?: string;
  /**
   * Multiple permissions - user needs ANY of these (OR logic)
   */
  anyPermission?: string[];
  /**
   * Multiple permissions - user needs ALL of these (AND logic)
   */
  allPermissions?: string[];
  /**
   * Role-based check - user needs this role or higher
   */
  role?: string;
  /**
   * Role-based check - user needs any of these roles
   */
  anyRole?: string[];
  /**
   * Content to render when user has permission
   */
  children: ReactNode;
  /**
   * Content to render when user does NOT have permission
   * @default null (renders nothing)
   */
  fallback?: ReactNode;
}

/**
 * Component to conditionally render content based on user permissions
 *
 * @example
 * // Single permission
 * <PermissionGate permission="products:create">
 *   <CreateProductButton />
 * </PermissionGate>
 *
 * @example
 * // Any of multiple permissions
 * <PermissionGate anyPermission={["products:create", "products:update"]}>
 *   <EditSection />
 * </PermissionGate>
 *
 * @example
 * // All permissions required
 * <PermissionGate allPermissions={["products:delete", "inventory:adjust"]}>
 *   <DangerZone />
 * </PermissionGate>
 *
 * @example
 * // Role-based with fallback
 * <PermissionGate role="STORE_MANAGER" fallback={<AccessDenied />}>
 *   <ManagerDashboard />
 * </PermissionGate>
 */
export function PermissionGate({
  permission,
  anyPermission,
  allPermissions,
  role,
  anyRole,
  children,
  fallback = null,
}: PermissionGateProps) {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
  } = usePermissions();

  let hasAccess = true;

  // Check single permission
  if (permission) {
    hasAccess = hasAccess && hasPermission(permission);
  }

  // Check any permission (OR)
  if (anyPermission && anyPermission.length > 0) {
    hasAccess = hasAccess && hasAnyPermission(anyPermission);
  }

  // Check all permissions (AND)
  if (allPermissions && allPermissions.length > 0) {
    hasAccess = hasAccess && hasAllPermissions(allPermissions);
  }

  // Check role
  if (role) {
    hasAccess = hasAccess && hasRole(role);
  }

  // Check any role
  if (anyRole && anyRole.length > 0) {
    hasAccess = hasAccess && hasAnyRole(anyRole);
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

/**
 * HOC to wrap a component with permission check
 *
 * @example
 * const ProtectedButton = withPermission(CreateButton, "products:create");
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  permission: string,
  Fallback?: React.ComponentType,
) {
  return function PermissionWrappedComponent(props: P) {
    return (
      <PermissionGate
        permission={permission}
        fallback={Fallback ? <Fallback /> : null}
      >
        <Component {...props} />
      </PermissionGate>
    );
  };
}
